import React, { useState } from "react"
import { Box, componentStyles } from "@mallardbay/lib-react-components"

import ResponsiveBlock from "~components/shared/responsive-block"
import Divider from "~components/shared/divider"
import DetailsBoxMobile from "~components/pages/listing/details-box-mobile"
import Host from "~components/pages/listing/host"
import ListingSuggestionsBasedOnListing from "~components/pages/listing/listing-suggestions-based-on-listing"
import { useCurrentListing } from "~components/providers/current-listing-provider"
import ListingHost from "~components/pages/listing/listing-host"
import ListingPricingDrawer from "~components/pages/listing/listing-pricing-drawer"
import ListingFeed from "~components/pages/listing/listing-feed"
import PackageSections from "~components/pages/listing/pricing/package-sections"
import ReportListingButton from "~components/pages/listing/pricing/report-listing-button"
import ListWithDividers from "~components/shared/list-with-dividers"
import type { PublicListingFieldsFragment } from "~graphql/generated/graphql"
import { useListingItems } from "~utils/hooks/use-listing-items"
import ListingContactBanner from "~components/pages/listing/listing-contact-banner"
import GenericCollection, {
    TYPES,
} from "~components/shared/collections/generic-collection"
import ProfileCard from "~components/shared/profile-card"
import { formatEnumToTitleCase } from "~utils/formatting-helpers"
import useOwnersAndCoOwners from "~utils/use-owners-and-co-owners"
import { TRACKING_SOURCES } from "~config/constants"

export default function ListingDetails() {
    const styles = useStyles()
    const { currentListing } = useCurrentListing()

    const [drawerIsOpen, setDrawerIsOpen] = useState(false)

    const {
        id,
        outfitter,
        published,
        is_unavailable: isUnavailable,
        next_availability: nextAvailability,
    } = currentListing

    const items = useItems({ currentListing })

    const { allOwners, isLoading } = useOwnersAndCoOwners({
        outfitterId: outfitter.id,
    })

    return (
        <>
            <ResponsiveBlock>
                <Box style={styles.grid}>
                    <Box as="main" style={styles.main}>
                        <Box style={styles.mobileFeed}>
                            <ListingFeed />
                            <Divider />
                        </Box>
                        <ListWithDividers items={items} />
                        <GenericCollection
                            type={TYPES.GRID}
                            isLoading={isLoading}
                            columns={[1, 2, 2, 3, 3]}
                        >
                            {allOwners.map((owner) => (
                                <ProfileCard
                                    key={owner.id}
                                    name={owner.name}
                                    avatar={owner.user?.avatar_url ?? undefined}
                                    bio={owner.user?.bio}
                                    position={formatEnumToTitleCase(owner.role)}
                                    experience={owner.user?.years_of_experience}
                                    socials={owner.user?.socials}
                                />
                            ))}
                        </GenericCollection>
                    </Box>
                    <Box as="aside" style={styles.aside}>
                        <PackageSections
                            hasNextAvailableDate={!isUnavailable}
                            listingId={id}
                            published={published || false}
                            outfitterName={outfitter.name}
                            // if there is no availability we can just hide the continue button
                            // since you cant book if there is no availability
                            hideContinueButton={!nextAvailability}
                        />
                        <ListingContactBanner
                            listing={currentListing}
                            trackingSource={TRACKING_SOURCES.MALLARD_BAY}
                        />
                        <ReportListingButton />
                        <ListingFeed />
                    </Box>
                </Box>
            </ResponsiveBlock>

            <ListingSuggestionsBasedOnListing listing={currentListing} />
            <DetailsBoxMobile
                listing={currentListing}
                setDrawerIsOpen={setDrawerIsOpen}
            />
            <ListingPricingDrawer
                isOpen={drawerIsOpen}
                onClose={() => {
                    setDrawerIsOpen(false)
                }}
            />
        </>
    )
}

function useStyles() {
    return componentStyles({
        mobileFeed: {
            display: { base: "block", md: "none" },
        },
        grid: {
            marginTop: "24px",
            marginBottom: "48px",
            display: "grid",
            gridTemplateColumns: { base: "100%", md: "64% 33%" },
            gap: "48px",
        },
        aside: {
            display: { base: "none", md: "block" },
            position: { md: "sticky" },
            top: { md: "96px" },
            alignSelf: { md: "start" },
        },
        main: {
            display: "flex",
            flexDirection: "column",
            gap: "24px",
        },
    })
}

function useItems({
    currentListing,
}: {
    currentListing: PublicListingFieldsFragment
}) {
    const commonItems = useListingItems({ listing: currentListing })

    return [
        {
            Component: <ListingHost />,
        },
        ...commonItems,
        {
            Component: <Host />,
        },
    ]
}
